import React, { useMemo } from 'react';

import { View } from 'react-native';

import { addMinutes, endOfDay, format, startOfDay } from 'date-fns';

import Text, { ETextCategory, ETextColor } from 'components/Text';

import styles from './styles';

type Props = {
    time: number;
    startTime: number;
    previousDateLabel: string;
    nextDateLabel: string;
};

const EpgHourCell = ({
    time,
    startTime,
    previousDateLabel,
    nextDateLabel,
}: Props) => {
    const labelOnTheStartOfTheCell = useMemo(() => {
        /** If the time is equal with the start of the day, the label of the previous day needs to be rendered
         on the left side of the cell container.
         */
        if (time === startOfDay(startTime).getTime()) {
            return previousDateLabel;
        }

        return format(time, 'HH:mm');
    }, [time, startTime, previousDateLabel]);

    const labelOnTheEnfOfTheCell = useMemo(() => {
        /** If the time is equal with the end of the day, the label of the next day needs to be shown on the right side of
         the cell */
        if (time === endOfDay(startTime).setMinutes(0, 0, 0)) {
            return nextDateLabel;
        }

        return null;
    }, [time, startTime, nextDateLabel]);

    const rightTextContainerStyle = useMemo(() => {
        if (time === endOfDay(startTime).setMinutes(0, 0, 0)) {
            return styles.rightCellTextContainer;
        }

        return null;
    }, [time, startTime]);

    const rightTextStyle = useMemo(() => {
        if (time === endOfDay(startTime).setMinutes(0, 0, 0)) {
            return styles.labelStyleRight;
        }

        return styles.textStyleRight;
    }, [time, startTime]);

    const leftTextStyle = useMemo(() => {
        if (time === startOfDay(startTime).getTime()) {
            return styles.labelStyleLeft;
        }

        return styles.textStyle;
    }, []);

    const leftTextContainerStyle = useMemo(() => {
        if (time === startOfDay(startTime).getTime()) {
            return styles.leftCellTextContainer;
        }

        return null;
    }, [time, startTime]);

    return (
        <View style={styles.container}>
            <View style={[styles.textContainer, leftTextContainerStyle]}>
                <Text
                    category={ETextCategory.body2}
                    color={ETextColor.neutral}
                    style={leftTextStyle}
                >
                    {labelOnTheStartOfTheCell}
                </Text>
            </View>
            <View style={styles.textContainer}>
                <Text
                    category={ETextCategory.body2}
                    color={ETextColor.neutral}
                    style={styles.textStyleCenter}
                >
                    {format(addMinutes(time, 30), 'HH:mm')}
                </Text>
            </View>
            <View style={[styles.textContainer, rightTextContainerStyle]}>
                <Text
                    category={ETextCategory.body2}
                    color={ETextColor.neutral}
                    style={rightTextStyle}
                >
                    {labelOnTheEnfOfTheCell}
                </Text>
            </View>
        </View>
    );
};

export default EpgHourCell;
